'use client';

import { Check, Copy, Eye } from 'lucide-react';
import { type MouseEvent, type ReactNode, useState } from 'react';

import professionalsServices from '@/services/professionals-services';

/**
 * Click-to-reveal contact row for the public advocate profile.
 *
 * Phone / email are hidden behind a "Show …" button by default so naive
 * scrapers can't harvest `tel:`/`mailto:` links from the static HTML. A single
 * frictionless click reveals the value (no login / tier wall — that would hurt
 * the directory's core job and touches marketplace-neutrality). The first
 * reveal fires the same ``contact_click`` engagement event the result cards
 * emit (#5371), so the reveal counts as high-intent in the claim funnel.
 *
 * Once revealed the value becomes a real `tel:`/`mailto:` link with a
 * copy-to-clipboard button.
 */
interface Props {
  icon: ReactNode;
  /** The contact value, e.g. "+421905702326" or "info@rhlegal.sk". */
  value: string;
  /** `tel:` / `mailto:` href used once revealed. */
  href: string;
  /** Localized "Show phone" / "Show email" label shown while hidden. */
  revealLabel: string;
  /** Localized aria-label for the copy button, e.g. "Copy phone number". */
  copyLabel: string;
  /** Localized "Copied" confirmation. */
  copiedLabel: string;
  professionalId: string;
}

export function RevealContactRow({
  icon,
  value,
  href,
  revealLabel,
  copyLabel,
  copiedLabel,
  professionalId,
}: Props) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleReveal = () => {
    setRevealed(true);
    // Fire-and-forget: a reveal is a high-intent contact signal. Mirrors the
    // result-card CTA so the claim/engagement funnel sees profile reveals too.
    void professionalsServices.logSearchEvent({
      event_type: 'contact_click',
      professional_id: professionalId,
    });
  };

  const handleCopy = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable (insecure context / denied) — no-op; the value
      // is still visible for manual copy.
    }
  };

  if (!revealed) {
    return (
      <button
        type='button'
        onClick={handleReveal}
        className='group inline-flex min-h-[44px] items-center gap-3 text-sm text-brand-tertiary/80 transition-colors hover:text-brand-tertiary'
      >
        <span className='flex-shrink-0 text-content-muted group-hover:text-brand-tertiary'>
          {icon}
        </span>
        <Eye className='h-4 w-4 flex-shrink-0' />
        <span className='font-medium'>{revealLabel}</span>
      </button>
    );
  }

  return (
    <div className='flex min-h-[44px] items-center gap-3 text-sm'>
      <span className='flex-shrink-0 text-content-muted'>{icon}</span>
      <a
        href={href}
        className='break-all text-content-secondary transition-colors hover:text-brand-tertiary'
      >
        {value}
      </a>
      <button
        type='button'
        onClick={handleCopy}
        aria-label={copyLabel}
        title={copied ? copiedLabel : copyLabel}
        className='ml-auto flex-shrink-0 rounded-md p-1.5 text-content-muted transition-colors hover:bg-surface-muted hover:text-brand-tertiary'
      >
        {copied ? (
          <Check className='h-4 w-4 text-status-success' />
        ) : (
          <Copy className='h-4 w-4' />
        )}
      </button>
    </div>
  );
}
